/**
 * Gargalos do funil (#557): onde as oportunidades param.
 *
 * ── Primeiro a foto, por etapa ────────────────────────────────────────────
 *
 * Um cartão por etapa, na ordem do funil: quantas estão ali agora, a barra com
 * a parte PARADA em laranja (o acento: o que merece o olho), e o botão que
 * abre as paradas. A etapa com a maior PROPORÇÃO de paradas ganha o fundo
 * laranja e diz "maior gargalo agora" — proporção, e não quantidade, porque
 * dez paradas em cem é menos grave que cinco em seis.
 *
 * ── Depois foto e fluxo lado a lado ───────────────────────────────────────
 *
 * Na tabela, as três primeiras colunas são a FOTO de agora: quantas estão na
 * etapa, quantas paradas e há quanto tempo está a mais antiga. As três últimas
 * são o FLUXO do período: quantas entraram, quantas avançaram e quanto tempo
 * levaram para sair. O cabeçalho separa as duas — com fundo próprio cada uma —,
 * porque as perguntas são diferentes e confundi-las é o erro mais comum de
 * quem lê funil.
 *
 * ── Todo número de parada leva às oportunidades ───────────────────────────
 *
 * O botão de cada etapa, ou o nome de uma pessoa, abre a lista das
 * oportunidades que o formam, pela mesma regra e com o mesmo corte.
 */

import { useState, type ReactNode } from 'react';
import type { EtapaComGargalo, GargalosDoFunil } from '@cliqcrm/contracts';
import { useGargalosDoFunil, useOportunidadesParadas } from '@/shared/api/consultas';
import { Estado } from '@/shared/ui/estados/estados';
import { Avatar, CabecalhoDeTela, CampoSelecao, Dialogo } from '@/shared/ui';
import { nomeDaEtapa } from '@/entities/comercial/etapa';
import { diaPorExtenso, diasDoPeriodo, PERIODO_PADRAO, type ChaveDePeriodo } from '../lib/periodo';
import { BarraDoRelatorio, TituloDoCartao } from './barra';
import { ABRANGENCIA, dinheiro, horasEmPalavras, porcentagem } from '../lib/formato';
import { BotaoExportar } from './exportar';
import { nomeDaPlanilha, planilhaDosGargalos } from '../lib/planilhas';
import './relatorios.css';

const CORTES = ['3', '7', '15', '30'] as const;

interface Detalhe {
  readonly titulo: string;
  readonly etapa?: string;
  readonly responsavel?: string;
}

const paradasEmPalavras = (n: number): string => (n === 1 ? '1 parada' : `${n} paradas`);

export function GargalosTela(): ReactNode {
  const [periodo, definirPeriodo] = useState<ChaveDePeriodo>(PERIODO_PADRAO);
  const [funil, definirFunil] = useState('');
  const [corte, definirCorte] = useState<(typeof CORTES)[number]>('7');
  const [detalhe, definirDetalhe] = useState<Detalhe | null>(null);

  const consulta = useGargalosDoFunil({
    dias: String(diasDoPeriodo(periodo)),
    parada_dias: corte,
    ...(funil ? { funil } : {}),
  });

  return (
    <div className="relatorio">
      <CabecalhoDeTela
        titulo="Gargalos do funil"
        apoio="Onde as oportunidades param, e quem tem negócio parado."
        acoes={
          <BotaoExportar
            planilha={
              consulta.data
                ? () => ({
                    conteudo: planilhaDosGargalos(consulta.data!),
                    nome: nomeDaPlanilha('gargalos', consulta.data!.periodo),
                  })
                : null
            }
          />
        }
        {...(consulta.data
          ? {
              meta: (
                <span>
                  Fluxo de {diaPorExtenso(consulta.data.periodo.de)} a{' '}
                  {diaPorExtenso(consulta.data.periodo.ate)}
                </span>
              ),
            }
          : {})}
        extra={
          <BarraDoRelatorio
            periodo={periodo}
            aoEscolherPeriodo={definirPeriodo}
            {...(consulta.data ? { abrangencia: ABRANGENCIA[consulta.data.abrangencia] } : {})}
          >
            {/* Os filtros moram FORA da área que carrega: dentro dela, trocar de
                funil fazia o campo sumir debaixo do cursor. */}
            {consulta.data && consulta.data.funis.length > 1 && (
              <span className="relatorio__filtro">
                <CampoSelecao
                  rotulo="Funil"
                  tamanho="miudo"
                  value={funil || consulta.data.funil?.chave || ''}
                  onChange={(evento) => definirFunil(evento.target.value)}
                >
                  {consulta.data.funis.map((f) => (
                    <option key={f.chave} value={f.chave}>
                      {f.nome}
                    </option>
                  ))}
                </CampoSelecao>
              </span>
            )}
            <span className="relatorio__filtro">
              <CampoSelecao
                rotulo="Parada há"
                tamanho="miudo"
                ativo
                value={corte}
                onChange={(evento) => definirCorte(evento.target.value as (typeof CORTES)[number])}
              >
                {CORTES.map((c) => (
                  <option key={c} value={c}>
                    {c} dias ou mais
                  </option>
                ))}
              </CampoSelecao>
            </span>
          </BarraDoRelatorio>
        }
      />

      <div className="relatorio__corpo">
        <Estado consulta={consulta} forma="cartoes" linhas={3}>
          {(dados) =>
            !dados.funil ? (
              <p className="relatorio__vazio">Nenhum funil configurado nesta organização.</p>
            ) : (
              <>
                <EtapasAgora dados={dados} aoAbrir={definirDetalhe} />
                <TabelaDeEtapas dados={dados} />
                <Responsaveis dados={dados} aoAbrir={definirDetalhe} />
              </>
            )
          }
        </Estado>
      </div>

      {detalhe && consulta.data?.funil && (
        <Paradas
          detalhe={detalhe}
          filtro={{
            funil: consulta.data.funil.chave,
            parada_dias: String(consulta.data.parada_dias),
            ...(detalhe.etapa ? { etapa: detalhe.etapa } : {}),
            ...(detalhe.responsavel ? { responsavel: detalhe.responsavel } : {}),
          }}
          aoFechar={() => definirDetalhe(null)}
        />
      )}
    </div>
  );
}

/** A etapa com a maior proporção de paradas; nenhuma quando não há parada. */
function piorEtapa(etapas: EtapaComGargalo[]): string | null {
  let pior: EtapaComGargalo | null = null;
  for (const e of etapas) {
    if (e.paradas === 0 || e.em_aberto === 0) continue;
    if (!pior || e.paradas / e.em_aberto > pior.paradas / pior.em_aberto) pior = e;
  }
  return pior?.etapa ?? null;
}

function EtapasAgora({
  dados,
  aoAbrir,
}: {
  dados: GargalosDoFunil;
  aoAbrir: (detalhe: Detalhe) => void;
}): ReactNode {
  const maior = Math.max(1, ...dados.etapas.map((e) => e.em_aberto));
  const pior = piorEtapa(dados.etapas);

  return (
    <section className="relatorio__grupo" aria-labelledby="gargalos-agora">
      <TituloDoCartao
        id="gargalos-agora"
        apoio={
          <span className="etapas-agora__legenda">
            <span className="etapas-agora__chave etapas-agora__chave--andando" aria-hidden="true" />
            andando
            <span className="etapas-agora__chave etapas-agora__chave--parada" aria-hidden="true" />
            paradas há {dados.parada_dias}+ dias
          </span>
        }
      >
        Agora, por etapa
      </TituloDoCartao>
      <ol className="etapas-agora">
        {dados.etapas.map((etapa, indice) => {
          const nome = nomeDaEtapa(etapa.nome);
          const ehPior = etapa.etapa === pior;
          return (
            <li
              key={etapa.etapa}
              className={
                ehPior ? 'etapas-agora__item etapas-agora__item--pior' : 'etapas-agora__item'
              }
            >
              <span className="etapas-agora__nome">
                {indice + 1} · {nome}
              </span>
              <span className="etapas-agora__numero">
                {etapa.em_aberto} <span className="etapas-agora__unidade">na etapa</span>
              </span>
              <span className="etapas-agora__trilho" aria-hidden="true">
                <span
                  className="etapas-agora__andando"
                  style={{ width: `${((etapa.em_aberto - etapa.paradas) / maior) * 100}%` }}
                />
                <span
                  className="etapas-agora__parada"
                  style={{ width: `${(etapa.paradas / maior) * 100}%` }}
                />
              </span>
              {etapa.paradas === 0 ? (
                <span className="etapas-agora__sem-parada">nenhuma parada</span>
              ) : (
                <button
                  type="button"
                  className="etapas-agora__botao"
                  aria-label={`${etapa.paradas} paradas em ${nome}. Ver quais`}
                  onClick={() => aoAbrir({ titulo: `Paradas em ${nome}`, etapa: etapa.etapa })}
                >
                  {paradasEmPalavras(etapa.paradas)} · ver quais
                </button>
              )}
              <span className="etapas-agora__antiga">
                mais antiga:{' '}
                {etapa.mais_antiga_dias === null ? '—' : `${etapa.mais_antiga_dias} dias`}
              </span>
              {ehPior && <span className="etapas-agora__selo">maior gargalo agora</span>}
            </li>
          );
        })}
      </ol>
    </section>
  );
}

function TabelaDeEtapas({ dados }: { dados: GargalosDoFunil }): ReactNode {
  return (
    <section className="relatorio__cartao" aria-labelledby="gargalos-fluxo">
      <TituloDoCartao id="gargalos-fluxo">Foto e fluxo</TituloDoCartao>
      <div className="relatorio__tabela relatorio__tabela--solta">
        <table className="fluxo">
          <caption className="visualmente-oculto">Etapas de {dados.funil?.nome}</caption>
          <thead>
            <tr>
              <th scope="col" rowSpan={2}>
                Etapa
              </th>
              <th scope="colgroup" colSpan={3} className="fluxo__grupo fluxo__grupo--agora">
                Agora
              </th>
              <th scope="colgroup" colSpan={3} className="fluxo__grupo fluxo__grupo--periodo">
                No período
              </th>
            </tr>
            <tr>
              <th scope="col" className="relatorio__num fluxo__agora">
                Na etapa
              </th>
              <th scope="col" className="relatorio__num fluxo__agora">
                Paradas há {dados.parada_dias}+ dias
              </th>
              <th scope="col" className="relatorio__num fluxo__agora">
                Mais antiga
              </th>
              <th scope="col" className="relatorio__num fluxo__periodo">
                Entraram
              </th>
              <th scope="col" className="relatorio__num fluxo__periodo">
                Avançaram
              </th>
              <th scope="col" className="relatorio__num fluxo__periodo">
                Tempo médio
              </th>
            </tr>
          </thead>
          <tbody>
            {dados.etapas.map((etapa) => (
              <tr key={etapa.etapa}>
                <th scope="row">{nomeDaEtapa(etapa.nome)}</th>
                <td className="relatorio__num">{etapa.em_aberto}</td>
                <td
                  className={etapa.paradas > 0 ? 'relatorio__num fluxo__paradas' : 'relatorio__num'}
                >
                  {etapa.paradas}
                </td>
                <td className="relatorio__num">
                  {etapa.mais_antiga_dias === null ? '—' : `${etapa.mais_antiga_dias} dias`}
                </td>
                <td className="relatorio__num">{etapa.entradas}</td>
                <td className="relatorio__num">
                  {`${etapa.avancaram} (${porcentagem(etapa.avancaram, etapa.entradas)})`}
                </td>
                <td className="relatorio__num">{horasEmPalavras(etapa.tempo_medio_horas)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="relatorio__nota">
        “Agora” é a foto deste instante; “No período” é o que passou pela etapa no período
        escolhido. São perguntas diferentes.
      </p>
    </section>
  );
}

function Responsaveis({
  dados,
  aoAbrir,
}: {
  dados: GargalosDoFunil;
  aoAbrir: (detalhe: Detalhe) => void;
}): ReactNode {
  const maior = Math.max(1, ...dados.responsaveis.map((p) => p.paradas));
  return (
    <section className="relatorio__cartao" aria-labelledby="gargalos-responsaveis">
      <TituloDoCartao id="gargalos-responsaveis">Quem tem oportunidade parada</TituloDoCartao>
      {dados.responsaveis.length === 0 ? (
        <p className="relatorio__vazio">
          Nenhuma oportunidade parada há {dados.parada_dias} dias ou mais.
        </p>
      ) : (
        <ul className="relatorio__pessoas">
          {dados.responsaveis.map((pessoa) => {
            // Sem referência é negócio sem dono; com referência e sem nome, é um
            // dono que não está (ou não está mais) na equipe.
            const nome =
              pessoa.nome ??
              (pessoa.responsavel_referencia_externa
                ? 'Responsável não identificado'
                : 'Sem responsável');
            return (
              <li
                key={pessoa.responsavel_referencia_externa ?? 'sem-responsavel'}
                className="relatorio__pessoas-item"
              >
                {pessoa.responsavel_referencia_externa ? (
                  <button
                    type="button"
                    className="relatorio__pessoa"
                    onClick={() =>
                      aoAbrir({
                        titulo: `Paradas de ${nome}`,
                        responsavel: pessoa.responsavel_referencia_externa!,
                      })
                    }
                  >
                    <Avatar nome={nome} tamanho={28} foto={pessoa.foto_url} />
                    <span>{nome}</span>
                  </button>
                ) : (
                  <span className="relatorio__pessoa relatorio__pessoa--fixa">
                    <Avatar nome={nome} tamanho={28} />
                    <span>{nome}</span>
                  </span>
                )}
                <span className="relatorio__contagem relatorio__contagem--parada">
                  {paradasEmPalavras(pessoa.paradas)}
                </span>
                <span className="relatorio__pessoas-trilho" aria-hidden="true">
                  <span
                    className="relatorio__pessoas-barra"
                    style={{ width: `${(pessoa.paradas / maior) * 100}%` }}
                  />
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function Paradas({
  detalhe,
  filtro,
  aoFechar,
}: {
  detalhe: Detalhe;
  filtro: Record<string, string>;
  aoFechar: () => void;
}): ReactNode {
  const consulta = useOportunidadesParadas(filtro);
  return (
    <Dialogo titulo={detalhe.titulo} aoFechar={aoFechar} largura="larga">
      <Estado consulta={consulta} forma="lista" linhas={4}>
        {(dados) =>
          dados.itens.length === 0 ? (
            <p className="relatorio__vazio">Nenhuma oportunidade parada aqui.</p>
          ) : (
            <ul className="relatorio__vendas">
              {dados.itens.map((op) => (
                <li key={op.referencia_externa}>
                  <span className="relatorio__venda-titulo">{op.titulo ?? 'Sem título'}</span>
                  <span className="relatorio__venda-meta">
                    {nomeDaEtapa(op.etapa_nome)} há {op.dias_na_etapa} dias
                    {op.responsavel_nome ? ` · ${op.responsavel_nome}` : ''}
                  </span>
                  <span className="relatorio__num">
                    {op.valor_micros === null
                      ? 'sem valor'
                      : dinheiro(op.valor_micros, op.moeda ?? 'BRL')}
                  </span>
                </li>
              ))}
            </ul>
          )
        }
      </Estado>
    </Dialogo>
  );
}
